<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import SubscriptionAPI from 'dashboard/api/subscription';
import { useAlert } from 'dashboard/composables';
import Icon from 'dashboard/components-next/icon/Icon.vue';
// TODO: Add PayPal icon
const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const loading = ref(true);
const payments = ref([]);

const fetchPaymentHistory = async () => {
  try {
    loading.value = true;
    const response = await SubscriptionAPI.getPaymentHistory();

    if (response.data.success) {
      payments.value = response.data.payments || [];
    } else {
      useAlert(t('SUBSCRIPTION_SETTINGS.PAYMENT_HISTORY.ERROR'));
    }
  } catch (error) {
    useAlert(t('SUBSCRIPTION_SETTINGS.PAYMENT_HISTORY.ERROR'));
  } finally {
    loading.value = false;
  }
};

const formatDate = date => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatAmount = amount => {
  if (!amount) return '0.00';
  return parseFloat(amount).toFixed(2);
};

const getBillingTypeLabel = type => {
  const labels = {
    monthly: t('SUBSCRIPTION_SETTINGS.PAYMENT_HISTORY.BILLING_TYPE.MONTHLY'),
    yearly: t('SUBSCRIPTION_SETTINGS.PAYMENT_HISTORY.BILLING_TYPE.YEARLY'),
  };
  return labels[type] || type;
};

const getBillingTypeClass = type => {
  const classes = {
    monthly: 'bg-blue-100 text-blue-800',
    yearly: 'bg-purple-100 text-purple-800',
  };
  return classes[type] || 'bg-n-slate-3 text-n-slate-11';
};

const getStatusLabel = status => {
  const labels = {
    completed: t('SUBSCRIPTION_SETTINGS.PAYMENT_HISTORY.STATUS.COMPLETED'),
    pending: t('SUBSCRIPTION_SETTINGS.PAYMENT_HISTORY.STATUS.PENDING'),
    failed: t('SUBSCRIPTION_SETTINGS.PAYMENT_HISTORY.STATUS.FAILED'),
  };
  return labels[status] || status;
};

const getStatusClass = status => {
  const classes = {
    completed: 'bg-green-100 text-green-800',
    pending: 'bg-yellow-100 text-yellow-800',
    failed: 'bg-red-100 text-red-800',
  };
  return classes[status] || 'bg-n-slate-3 text-n-slate-11';
};

const goBack = () => {
  router.push({
    name: 'subscription_settings',
    params: { accountId: route.params.accountId },
  });
};

onMounted(async () => {
  await fetchPaymentHistory();
});
</script>

<template>
  <div class="flex flex-col h-full max-w-6xl">
    <div class="flex items-center justify-between mb-6">
      <div>
        <h2 class="text-2xl font-semibold text-n-slate-12">
          {{ $t('SUBSCRIPTION_SETTINGS.PAYMENT_HISTORY.TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-11 mt-1">
          {{ $t('SUBSCRIPTION_SETTINGS.PAYMENT_HISTORY.DESCRIPTION') }}
        </p>
      </div>
      <button
        class="flex items-center gap-2 px-4 py-2 text-sm font-medium text-n-slate-12 border border-n-slate-6 rounded-lg hover:bg-n-slate-2 transition-colors"
        @click="goBack"
      >
        <Icon icon="i-lucide-arrow-left" class="w-4 h-4" />
        {{ $t('SUBSCRIPTION_SETTINGS.PAYMENT_HISTORY.BACK') }}
      </button>
    </div>

    <div v-if="loading" class="flex items-center justify-center py-12">
      <div
        class="animate-spin rounded-full h-8 w-8 border-b-2 border-n-brand"
      />
    </div>

    <div
      v-else-if="!loading && payments.length === 0"
      class="flex flex-col items-center justify-center py-16 border border-n-slate-6 rounded-xl bg-n-slate-1"
    >
      <Icon icon="i-lucide-receipt" class="w-16 h-16 text-n-slate-9 mb-4" />
      <p class="text-lg font-medium text-n-slate-12">
        {{ $t('SUBSCRIPTION_SETTINGS.PAYMENT_HISTORY.EMPTY_STATE.TITLE') }}
      </p>
      <p class="text-sm text-n-slate-11 mt-2 text-center max-w-md">
        {{ $t('SUBSCRIPTION_SETTINGS.PAYMENT_HISTORY.EMPTY_STATE.MESSAGE') }}
      </p>
    </div>

    <div v-else class="overflow-hidden rounded-lg border border-n-slate-6">
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-n-slate-6">
          <thead class="bg-n-slate-2">
            <tr>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider"
              >
                {{ $t('SUBSCRIPTION_SETTINGS.PAYMENT_HISTORY.TABLE.DATE') }}
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider"
              >
                {{ $t('SUBSCRIPTION_SETTINGS.PAYMENT_HISTORY.TABLE.PLAN') }}
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider"
              >
                {{ $t('SUBSCRIPTION_SETTINGS.PAYMENT_HISTORY.TABLE.AMOUNT') }}
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider"
              >
                {{ $t('SUBSCRIPTION_SETTINGS.PAYMENT_HISTORY.TABLE.TYPE') }}
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider"
              >
                {{ $t('SUBSCRIPTION_SETTINGS.PAYMENT_HISTORY.TABLE.METHOD') }}
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider"
              >
                {{ $t('SUBSCRIPTION_SETTINGS.PAYMENT_HISTORY.TABLE.STATUS') }}
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-n-slate-11 uppercase tracking-wider"
              >
                {{
                  $t('SUBSCRIPTION_SETTINGS.PAYMENT_HISTORY.TABLE.TRANSACTION')
                }}
              </th>
            </tr>
          </thead>
          <tbody class="bg-n-slate-1 divide-y divide-n-slate-6">
            <tr
              v-for="payment in payments"
              :key="payment.id"
              class="hover:bg-n-slate-2 transition-colors"
            >
              <td class="px-6 py-4 whitespace-nowrap text-sm text-n-slate-12">
                {{ formatDate(payment.paid_at || payment.created_at) }}
              </td>

              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex flex-col">
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ payment.plan_name }}
                  </span>
                  <span
                    v-if="payment.months && payment.months > 1"
                    class="text-xs text-n-slate-11"
                  >
                    {{
                      `${payment.months} ${$t('SUBSCRIPTION_SETTINGS.PAYMENT_HISTORY.MONTHS')}`
                    }}
                  </span>
                </div>
              </td>

              <td class="px-6 py-4 whitespace-nowrap">
                <span class="text-sm font-semibold text-n-slate-12">
                  {{ `${formatAmount(payment.amount)} ${payment.currency}` }}
                </span>
              </td>

              <td class="px-6 py-4 whitespace-nowrap">
                <span
                  class="px-2 py-1 text-xs font-medium rounded-full"
                  :class="getBillingTypeClass(payment.billing_type)"
                >
                  {{ getBillingTypeLabel(payment.billing_type) }}
                </span>
              </td>

              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center gap-2">
                  <Icon
                    v-if="payment.payment_method === 'paypal'"
                    icon="i-logos-paypal"
                    class="w-5 h-5"
                  />
                  <span class="text-sm text-n-slate-11 capitalize">
                    {{ payment.payment_method }}
                  </span>
                </div>
              </td>

              <td class="px-6 py-4 whitespace-nowrap">
                <span
                  class="px-2 py-1 text-xs font-medium rounded-full"
                  :class="getStatusClass(payment.status)"
                >
                  {{ getStatusLabel(payment.status) }}
                </span>
              </td>

              <td class="px-6 py-4 whitespace-nowrap">
                <span
                  v-if="payment.payment_provider_id"
                  class="text-xs font-mono text-n-slate-11 truncate max-w-[150px] block"
                  :title="payment.payment_provider_id"
                >
                  {{ payment.payment_provider_id }}
                </span>
                <span v-else class="text-xs text-n-slate-9">{{ '-' }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
